import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription, interval } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { ToastService } from '../../../../core/services/toast.service';
import { CoachingRealtimeService } from '../../services/coaching-realtime.service';
import { CoachMessage, CoachingService, MyCoach, ThreadDetail } from '../../services/coaching.service';
import { initials, isStaff } from '../../services/chat-utils';
import { CoachingChatThreadComponent } from '../coaching-chat-thread/coaching-chat-thread.component';

const THREAD_POLL_MS = 45000;
const MOBILE_BP = 768;

/**
 * End-user coaching messenger:
 * - Desktop: right dock, collapsed to a strip by default
 * - Phone: floating button → popup window
 * Open/closed state lives in CoachingRealtimeService (`drawerOpen$`) so the
 * "Message" buttons on /coaches can expand it.
 */
@Component({
  selector: 'app-coaching-messenger',
  standalone: true,
  imports: [CommonModule, CoachingChatThreadComponent],
  templateUrl: './coaching-messenger.component.html',
  styleUrls: ['./coaching-messenger.component.scss']
})
export class CoachingMessengerComponent implements OnInit, OnDestroy {
  coaches: MyCoach[] = [];
  unread = 0;
  open = false;
  hideForRoute = false;
  isAuthed = false;
  isMobile = false;
  refreshing = false;

  active: MyCoach | null = null;
  thread: ThreadDetail | null = null;
  loadingThread = false;
  loadingOlder = false;
  /** Visually hidden live region: "New message from …" for threads that aren't open. */
  announcement = '';

  readonly initials = initials;

  private destroy$ = new Subject<void>();
  private poll?: Subscription;
  private myId: string | null = null;
  private requestedThreadId: string | null = null;

  constructor(
    private realtime: CoachingRealtimeService,
    private coaching: CoachingService,
    private auth: AuthService,
    private toast: ToastService,
    private router: Router,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.updateViewport();
    this.myId = this.auth.currentUserId;
    this.auth.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.isAuthed = !!user;
      this.myId = user?.id ?? null;
    });

    this.realtime.drawerOpen$.pipe(takeUntil(this.destroy$)).subscribe(open => {
      const wasOpen = this.open;
      this.open = open;
      if (!open) {
        // Collapsed / closed: nothing is visible, so nothing is focused.
        this.leaveThread();
      } else if (!wasOpen) {
        this.realtime.refreshCoaches();
      }
    });

    this.realtime.userUnreadTotal$.pipe(takeUntil(this.destroy$)).subscribe(n => (this.unread = n));
    this.realtime.coaches$.pipe(takeUntil(this.destroy$)).subscribe(list => {
      this.coaches = list;
      if (this.active) {
        this.active = list.find(c => c.thread_id === this.active?.thread_id) || this.active;
      }
      this.tryOpenRequested();
    });

    // Only opens the thread — the service already opened the drawer.
    this.realtime.openThreadRequest$.pipe(takeUntil(this.destroy$)).subscribe(threadId => {
      this.requestedThreadId = threadId;
      this.tryOpenRequested();
    });

    this.realtime.messages$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event.thread_id === this.active?.thread_id && this.thread) {
        this.appendMessage(event.message);
        return;
      }
      if (this.open && event.message.from_user === false) {
        const coach = this.coaches.find(c => c.thread_id === event.thread_id)?.coach;
        this.announce(`New message from ${coach?.name || 'your coach'}`);
      }
    });

    this.realtime.messageStatus$.pipe(takeUntil(this.destroy$)).subscribe(e => {
      if (this.thread && e.thread_id === this.thread.thread_id) {
        this.thread = {
          ...this.thread,
          messages: this.thread.messages.map(m =>
            m.id === e.message_id ? { ...m, status: e.status, error_message: e.error ?? m.error_message } : m)
        };
      }
    });

    this.realtime.resync$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.active?.thread_id) {
        this.fetch(this.active.thread_id, false);
      }
    });

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(e => this.updateRouteVisibility(e.urlAfterRedirects));
    this.updateRouteVisibility(this.router.url);
  }

  ngOnDestroy(): void {
    this.poll?.unsubscribe();
    this.realtime.setFocusedThread(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  updateViewport(): void {
    this.isMobile = window.innerWidth < MOBILE_BP;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open && this.isMobile) {
      this.close();
    }
  }

  get hasCoaches(): boolean {
    return this.coaches.some(c => !!c.thread_id);
  }

  get showShell(): boolean {
    // Admins/trainers use the global unread dock + coaching inbox instead.
    if (isStaff(this.auth.getRoles())) {
      return false;
    }
    return this.isAuthed && this.hasCoaches && !this.hideForRoute;
  }

  /** Mobile FAB only */
  get showFab(): boolean {
    return this.showShell && this.isMobile;
  }

  /** Desktop collapsed strip */
  get showStrip(): boolean {
    return this.showShell && !this.isMobile && !this.open;
  }

  /** Expanded panel: desktop dock or mobile popup */
  get showPanel(): boolean {
    return this.showShell && this.open;
  }

  toggle(): void {
    this.realtime.toggleDrawer();
  }

  close(): void {
    this.realtime.closeDrawer();
  }

  refresh(): void {
    this.refreshing = true;
    this.realtime.refreshCoaches();
    if (this.active?.thread_id) {
      this.fetch(this.active.thread_id, false);
    }
    setTimeout(() => (this.refreshing = false), 600);
  }

  openThread(coach: MyCoach): void {
    if (!coach.thread_id) {
      return;
    }
    if (this.active?.thread_id === coach.thread_id && this.thread) {
      this.realtime.setFocusedThread(coach.thread_id);
      return;
    }
    this.active = coach;
    this.thread = null;
    this.loadingThread = true;
    this.realtime.setFocusedThread(coach.thread_id);
    this.fetch(coach.thread_id, true);

    this.poll?.unsubscribe();
    this.poll = interval(THREAD_POLL_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!document.hidden && this.active?.thread_id && !this.realtime.socketConnected) {
        this.fetch(this.active.thread_id, false);
      }
    });
  }

  backToList(): void {
    const threadId = this.active?.thread_id;
    this.leaveThread();
    this.realtime.refreshCoaches();
    // Return focus to the row the user came from.
    setTimeout(() => {
      const row = this.host.nativeElement.querySelector<HTMLElement>(`[data-thread-id="${threadId}"]`);
      row?.focus();
    });
  }

  /** User's own messages on the right. */
  readonly isOutgoing = (m: CoachMessage): boolean => {
    if (this.myId) {
      return m.sender_id === this.myId;
    }
    return !!m.from_user;
  };

  readonly sendFn = (content: string) => this.coaching.sendToMyCoach(this.active!.thread_id!, content);

  onSent(message: CoachMessage): void {
    this.appendMessage(message);
  }

  loadOlder(): void {
    const thread = this.thread;
    if (!thread?.messages.length || this.loadingOlder) {
      return;
    }
    this.loadingOlder = true;
    this.coaching.getMyCoachThread(thread.thread_id, thread.messages[0].created_on).pipe(takeUntil(this.destroy$)).subscribe({
      next: detail => {
        this.loadingOlder = false;
        if (this.thread?.thread_id !== thread.thread_id) {
          return;
        }
        const known = new Set(this.thread.messages.map(m => m.id));
        this.thread = {
          ...this.thread,
          has_more: detail.has_more,
          messages: [...detail.messages.filter(m => !known.has(m.id)), ...this.thread.messages]
        };
      },
      error: () => {
        this.loadingOlder = false;
        this.toast.showError('Could not load earlier messages');
      }
    });
  }

  trackCoach(_: number, c: MyCoach): string {
    return c.thread_id || c.product_line;
  }

  private tryOpenRequested(): void {
    const id = this.requestedThreadId;
    if (!id) {
      return;
    }
    const coach = this.coaches.find(c => c.thread_id === id);
    if (!coach) {
      return; // coaches not loaded yet — retried when coaches$ emits
    }
    this.requestedThreadId = null;
    this.realtime.ackThreadRequest();
    this.openThread(coach);
  }

  private leaveThread(): void {
    this.poll?.unsubscribe();
    this.active = null;
    this.thread = null;
    this.loadingThread = false;
    this.realtime.setFocusedThread(null);
  }

  /** Append if not already present (HTTP send + socket often race with the same id). */
  private appendMessage(message: CoachMessage): boolean {
    if (!this.thread || !message?.id || message.thread_id && message.thread_id !== this.thread.thread_id) {
      return false;
    }
    if (this.thread.messages.some(m => m.id === message.id)) {
      return false;
    }
    this.thread = { ...this.thread, messages: [...this.thread.messages, message] };
    return true;
  }

  private fetch(threadId: string, initial: boolean): void {
    this.coaching.getMyCoachThread(threadId).pipe(takeUntil(this.destroy$)).subscribe({
      next: detail => {
        if (this.active?.thread_id !== threadId) {
          return;
        }
        if (this.thread && !initial) {
          // Keep older pages loaded with "Load earlier"
          const latest = new Set(detail.messages.map(m => m.id));
          const first = detail.messages[0]?.created_on;
          const older = this.thread.messages.filter(m => !latest.has(m.id) && (!first || m.created_on < first));
          detail = { ...detail, messages: [...older, ...detail.messages], has_more: older.length ? this.thread.has_more : detail.has_more };
        }
        this.thread = detail;
        this.loadingThread = false;
        // The server marks the thread read on GET.
        this.realtime.clearThreadUnread(threadId);
      },
      error: () => {
        this.loadingThread = false;
        if (initial) {
          this.toast.showError('Could not open the conversation');
        }
      }
    });
  }

  private announce(text: string): void {
    this.announcement = '';
    setTimeout(() => (this.announcement = text), 50);
  }

  private updateRouteVisibility(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    this.hideForRoute =
      path.startsWith('/coach-console') ||
      path.includes('/manage/coaching') ||
      path.includes('/admin/coaching') ||
      path.includes('/manage-trainer/coaching') ||
      path.startsWith('/auth') ||
      path.startsWith('/onboarding') ||
      path.startsWith('/subscription');
    if (this.hideForRoute && this.open) {
      this.realtime.closeDrawer();
    }
  }
}
